import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { isMobile, isTablet } from 'react-device-detect';
import axios from 'axios';
import ArenaLogo from '../../assets/svg/ArenaLogo';
import SEO from '../../components/SEO';
import className from '../../utils/className';
import NavExpander from '../../containers/NavExpander';
import StatusMessage from './StatusMessage';
import Config from '../../config';
import styles from './inspirationMachine.css';

function importAll(r) {
  return r.keys().map(item => {
    return item.replace('./', '../../assets/img/inspiration/');
  });
}

const IMAGES = importAll(
  require.context('../../assets/img/inspiration', false, /\.(png|jpe?g|svg)$/),
);

const COLORS = [
  '#fe00ff',
  '#00ff65',
  '#0000ff',
  '#4516ff',
  '#d1ffd3',
  '#fffa6e',
  '#fc3737',
  '#69785e',
];

class InspirationMachine extends Component {
  constructor(props) {
    super(props);

    this.state = {
      arenaTitle: Config.inspirationMachine[0].title,
      arenaSlug: Config.inspirationMachine[0].slug,
      error: null,
      canvasImg: null,
      colorIndex: 0,
      currentImage: null,
      errorMessage: false,
      hideCanvas: false,
      hideCursor: false,
      imagesLoading: false,
      isDrawing: false,
      isLoading: false,
      isImage: false,
      isColor: true,
      images: [],
      imageIndex: 0,
      initialClick: true,
      listOpen: false,
      mouseX: 0,
      mouseY: 0,
      scale: window.devicePixelRatio,
      successMessage: false,
    };
    this.canvasRef = React.createRef();
    this.cursorRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('load', this.resize);
    window.addEventListener('resize', this.resizeWindow);
    setTimeout(() => {
      this.resize();
    }, 100);
    this.fetchImages();
  }

  componentWillUnmount() {
    window.removeEventListener('load', this.resize);
    window.removeEventListener('resize', this.resizeWindow);
  }

  postAndRetry = async (url, body, retries = 5) => {
    let tries = 0;
    let error;

    const exponentialDelay = tries => {
      const delay = Math.pow(2, tries) * 100;
      const randomSum = (delay / 3) * Math.random(); // 0-33% of the delay
      return delay + randomSum;
    };

    while (!error) {
      try {
        const data = await new Promise((resolve, reject) => {
          (async () => {
            try {
              const { data } = await axios.post(url, body);
              resolve(data);
            } catch (err) {
              tries += 1;
              if (tries === retries) {
                reject(err);
              } else {
                setTimeout(reject, exponentialDelay(tries));
              }
            }
          })();
        });
        return data;
      } catch (err) {
        error = err;
      }
    }

    throw error;
  };

  postContent() {
    this.setState({ isLoading: true });
    this.postAndRetry('/.netlify/functions/arena', {
      source: this.state.canvasImg,
      content:
        'This was generated by using Inspiration Machine found at https://artdirection.show',
      title:
        'This was generated by using Inspiration Machine found at https://artdirection.show',
    })
      .then(() => {
        this.setState({ isLoading: false });
        this.setState({ hideCanvas: true, successMessage: true });
      })
      .catch(() => {
        this.setState({ hideCanvas: true, isLoading: false, errorMessage: true });
      });
  }

  resizeWindow = () => {
    this.resize();
  };

  resize = () => {
    const { offsetWidth, offsetHeight } = this.canvasRef.current.parentNode;

    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');

    const { scale } = this.state;
    const w = offsetWidth * scale;
    const h = offsetHeight * scale;

    canvas.width = w;
    canvas.height = h;

    ctx.scale(scale, scale);
  };

  moveCursor = event => {
    const pos = this.getPos(event.nativeEvent);

    this.setState({
      mouseX: pos.x,
      mouseY: pos.y,
    });
  };

  calculateAspectRatioFit = (srcWidth, srcHeight, maxWidth, maxHeight) => {
    const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);

    return { width: srcWidth * ratio, height: srcHeight * ratio };
  };

  fetchImages() {
    this.setState({ imagesLoading: true });

    Promise.all(IMAGES)
      .then(responses => {
        const promiseArray = responses.map(response => {
          return this.imagePromise(response);
        });
        Promise.all(promiseArray)
          .then(images => {
            return this.setState({ images });
          })
          .then(() => {
            this.setState({ imagesLoading: false, isLoading: false });
          });
      })
      .catch(() => {
        this.setState({ imagesLoading: false, isLoading: false });
      });
  }

  imagePromise = src => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.addEventListener('load', () => resolve(img));
      img.addEventListener('error', err => reject(err));
      img.src = src;
    });
  };

  draw = event => {
    const { isDrawing, currentImage, hideCursor, isColor, isImage } = this.state;

    if (hideCursor) {
      this.setState({ hideCursor: false });
    }

    if (isDrawing) {
      const canvas = this.canvasRef.current;
      const ctx = canvas.getContext('2d');
      const pos = this.getPos(event.nativeEvent);

      if (isImage) {
        this.drawImage(ctx, pos.x, pos.y, currentImage);
      }

      if (isColor) {
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      }
    }
  };

  drawImage(ctx, x, y, currentImage) {
    const img = currentImage;
    const size = !isMobile ? 600 : 300;
    const imgResize = this.calculateAspectRatioFit(img.width, img.height, size, size);
    const imgWidth = imgResize.width;
    const imgHeight = imgResize.height;
    ctx.drawImage(img, x - imgWidth / 2, y - imgHeight / 2, imgWidth, imgHeight);
  }

  save = () => {
    const canvas = this.canvasRef.current;
    const img = new Image();
    img.src = canvas.toDataURL('image/jpg');
    const canvasResize = this.calculateAspectRatioFit(canvas.width, canvas.height, 1000, 1000);

    this.imagePromise(img.src).then(img => {
      const resizedImg = new Image();
      const resizedCanvas = document.createElement('canvas');
      const ctx = resizedCanvas.getContext('2d');
      resizedCanvas.width = canvasResize.width;
      resizedCanvas.height = canvasResize.height;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvasResize.width, canvasResize.height);
      ctx.drawImage(img, 0, 0, canvasResize.width, canvasResize.height);
      resizedImg.src = resizedCanvas.toDataURL('image/jpg');

      this.imagePromise(resizedImg.src)
        .then(newImg => {
          this.setState({
            canvasImg: newImg.src,
          });
        })
        .then(() => {
          this.postContent();
        });
    });
  };

  startDrawing = event => {
    const { isImage, isColor, isLoading, colorIndex } = this.state;
    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = this.getPos(event.nativeEvent);

    if (!isLoading) {
      if (isImage) {
        const { imageIndex, images } = this.state;

        let currentIndex = imageIndex <= images.length - 1 ? imageIndex : 0;
        let currentPhoto = '';

        currentPhoto = images[currentIndex];

        this.setState({
          isDrawing: true,
          imageIndex: (currentIndex += 1),
          currentImage: currentPhoto,
        });

        this.drawImage(ctx, pos.x, pos.y, currentPhoto);
      }
    }

    if (isColor) {
      if (this.state.initialClick) {
        this.setState({
          isDrawing: true,
          initialClick: false,
        });
        ctx.lineWidth = 20;
        ctx.lineJoin = ctx.lineCap = 'round';
        ctx.strokeStyle = COLORS[this.state.colorIndex];
        ctx.moveTo(pos.x, pos.y);
      } else {
        this.setState(
          {
            colorIndex: colorIndex + 1 === COLORS.length ? 0 : colorIndex + 1,
            isDrawing: true,
          },
          () => {
            ctx.lineWidth = 20;
            ctx.lineJoin = ctx.lineCap = 'round';
            ctx.strokeStyle = COLORS[this.state.colorIndex];
            ctx.moveTo(pos.x, pos.y);
          },
        );
      }
    }
  };

  stopDrawing = () => {
    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    this.setState({ isDrawing: false });
  };

  getPos = event => {
    if (event.touches !== undefined) {
      const rect = event.target.getBoundingClientRect();
      return { x: event.touches[0].pageX - rect.left, y: event.touches[0].pageY - rect.top };
    } else {
      return { x: event.layerX, y: event.layerY };
    }
  };

  toggleList = () => {
    this.setState(prevState => ({
      listOpen: !prevState.listOpen,
    }));
  };

  toggleColor = () => {
    const { isColor } = this.state;

    if (this.state.imagesLoading) {
      this.setState({ isLoading: true });
    }

    if (!isColor) {
      this.setState({
        isColor: true,
        isImage: false,
      });
    }
  };

  toggleImage = () => {
    if (this.state.imagesLoading) {
      this.setState({ isLoading: true });
    }

    this.setState({ isImage: true, isColor: false });
  };

  closeList = () => {
    this.setState({
      listOpen: false,
    });
  };

  handleReset = () => {
    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!this.state.errorMessage) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    this.setState({ errorMessage: false, hideCanvas: false, successMessage: false });
  };

  onMouseLeave = () => {
    this.setState({ hideCursor: true });
  };

  render() {
    const {
      colorIndex,
      errorMessage,
      hideCanvas,
      hideCursor,
      isColor,
      isLoading,
      isImage,
      mouseX,
      mouseY,
      successMessage,
    } = this.state;

    const cursorStyle = {
      '--mouseX': `${mouseX - 15}px`,
      '--mouseY': `${mouseY - 15}px`,
      borderColor: COLORS[colorIndex],
    };

    const swatchStyle = {
      backgroundColor: COLORS[colorIndex],
    };

    return (
      <div {...className(hideCanvas && styles.hideToolbar)}>
        <SEO title="Inspiration Machine" path="/inspiration-machine" />
        <NavExpander hideFooter={true}>
          <div
            {...className(
              styles.wrapper,
              `inspo`,
              (isMobile || isTablet) && styles.touch,
              hideCursor && styles.hideColorCursor,
            )}>
            {successMessage && (
              <StatusMessage messageType="success" resetCanvas={this.handleReset} />
            )}
            {errorMessage && <StatusMessage messageType="error" resetCanvas={this.handleReset} />}
            <section
              onMouseMove={this.moveCursor}
              {...className(
                styles.wrapper,
                isLoading && styles.loading,
                isColor && styles.color,
                hideCanvas && styles.hide,
              )}>
              <div className={styles.content}>
                {isColor && (
                  <div ref={this.cursorRef} style={cursorStyle} className={styles.cursor} />
                )}
                {isLoading && (
                  <div className={styles.loaderWrapper}>
                    <div className={styles.loaderEmoji}>
                      <span aria-label="loader emoji" role="img" className={styles.rotatingEmoji}>
                        😵
                      </span>
                    </div>
                  </div>
                )}
                <canvas
                  className={styles.canvas}
                  ref={this.canvasRef}
                  onClick={this.closeList}
                  onMouseLeave={this.onMouseLeave}
                  onMouseDown={this.startDrawing}
                  onMouseUp={this.stopDrawing}
                  onMouseMove={this.draw}
                  onTouchStart={this.startDrawing}
                  onTouchEnd={this.stopDrawing}
                  onTouchMove={this.draw}
                />
              </div>
            </section>
          </div>
        </NavExpander>
        <div className={styles.toolBarLeft}>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <button
                {...className(styles.colorButton, isImage && styles.active)}
                onClick={this.toggleImage}>
                Image
              </button>
            </li>
            <li className={styles.listItem}>
              <button
                {...className(styles.colorButton, isColor && styles.active)}
                onClick={this.toggleColor}>
                <span style={swatchStyle} className={styles.swatch} />
                Color
              </button>
            </li>
          </ul>
        </div>
        <div className={styles.toolBarRight}>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <button className={styles.saveButton} onClick={this.save}>
                Save to <ArenaLogo />
              </button>
            </li>
          </ul>
        </div>
      </div>
    );
  }
}

export default withRouter(InspirationMachine);
